import { CapitalGainsReport } from "@/modules/reports/pages/capital-gains";
import { createFileRoute } from "@tanstack/react-router";
import {
  clientDetailsQueryOptions,
  distributorEmployeeByIdQueryOptions,
  generateReportJsonOptions,
  getDistributorBrandingOptions,
  getDistributorProfileOptions,
  getUserOptions,
} from "creso-core/hooks";
import { portfolioSourceSchema } from "creso-core/zod";
import { object, string } from "zod";

export const Route = createFileRoute(
  "/(auth)/(headless)/reports/clients/$id/capital-gains"
)({
  component: CapitalGainsReport,
  validateSearch: object({
    start_date: string(),
    end_date: string(),
    source: portfolioSourceSchema.optional(),
  }),
  loaderDeps: ({ search: { start_date, end_date, source } }) => ({
    source,
    start_date,
    end_date,
  }),
  loader: async ({
    context,
    params,
    deps: { start_date, end_date, source },
  }) => {
    const { queryClient } = context;
    const userPromise = queryClient.fetchQuery(getUserOptions(true));
    const clientPromise = queryClient.fetchQuery(
      clientDetailsQueryOptions(Number(params.id))
    );
    const distributorProfilePromise = queryClient.fetchQuery(
      getDistributorProfileOptions(true)
    );
    const distributorBrandingPromise = queryClient.fetchQuery(
      getDistributorBrandingOptions(true)
    );

    const client = await clientPromise;
    const user = await userPromise;
    const metadata =
      user?.metadata && typeof user.metadata === "object"
        ? (user.metadata as Record<string, unknown>)
        : null;
    const rawEmployeeId = metadata?.id ?? null;
    const employeeId =
      user?.user_type === "employee"
        ? typeof rawEmployeeId === "number"
          ? rawEmployeeId
          : typeof rawEmployeeId === "string"
            ? Number(rawEmployeeId)
            : undefined
        : undefined;

    const employeePromise =
      employeeId != null && user?.user_type === "employee"
        ? queryClient.fetchQuery(
            distributorEmployeeByIdQueryOptions(employeeId, { enabled: true })
          )
        : Promise.resolve(null);

    if (!client) {
      throw new Error("Client not found");
    }

    const reportDataPromise = queryClient.fetchQuery(
      generateReportJsonOptions<"capital_gains">({
        type: "capital_gains",
        source,
        start_date,
        end_date,
        investment_account_guid: client.investment_account ?? "",
      })
    );

    if (!client.investment_account) {
      throw new Error("Client has no investment account");
    }

    return {
      client,
      distributorProfile: await distributorProfilePromise,
      distributorBranding: await distributorBrandingPromise,
      employee: await employeePromise,
      reportData: await reportDataPromise,
    };
  },
});
